import React, { useState, useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { getCsrfToken } from '@/utils/csrf';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

interface EmojiData {
    id: number;
    emoji: string;
    name: string;
    enabled: boolean;
    admin_only?: boolean;
}

interface EmojiSelectorProps {
    messageId: number;
    onEmojiSelect: (emojiId: number, emoji: EmojiData, apiResponse?: any) => void; // apiResponse az API adatokkal
    userEmojiId?: number; // A felhasználó által már kiválasztott emoji ID
    emojis?: EmojiData[]; // Előre lekérdezett emojis lista (opcionális, ha nincs, akkor lekérdezi)
    disabled?: boolean;
    onRemoveEmoji?: () => void; // Callback amikor az emoji-t törli a user
}

const EmojiSelector: React.FC<EmojiSelectorProps> = ({ messageId, onEmojiSelect, userEmojiId, emojis: propsEmojis, disabled = false, onRemoveEmoji }) => {
    const [emojis, setEmojis] = useState<EmojiData[]>(propsEmojis || []);
    const [loading, setLoading] = useState(!propsEmojis);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Ha már van prop-ból az emojis, ne lekérdezzük
        if (propsEmojis && propsEmojis.length > 0) {
            setEmojis(propsEmojis);
            setLoading(false);
            return;
        }

        const fetchEmojis = async () => {
            try {
                const response = await fetch('/api/emojis');
                if (response.ok) {
                    const data = await response.json();
                    // Csak az engedélyezett emoji-kat mutassuk
                    const enabledEmojis = (data.emojis || []).filter((e: EmojiData) => e.enabled);
                    console.log('Loaded emojis:', enabledEmojis); // Debug
                    setEmojis(enabledEmojis);
                }
            } catch (error) {
                console.error('Error fetching emojis:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchEmojis();
    }, [propsEmojis]);

    const handleEmojiClick = async (emoji: EmojiData) => {
        try {
            const response = await fetch(`/api/messages/${messageId}/emoji_reactions`, {
                method: 'POST',
                body: JSON.stringify({ emoji_id: emoji.id }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken(),
                },
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Emoji reaction added:', data);
                onEmojiSelect(emoji.id, emoji, data); // Átadjuk az API adatokat is
                setIsOpen(false);
            }
        } catch (error) {
            console.error('Error selecting emoji:', error);
        }
    };

    const handleRemoveEmoji = async () => {
        if (!userEmojiId) return;

        try {
            const response = await fetch(`/api/messages/${messageId}/emoji_reactions/${userEmojiId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken(),
                },
            });

            if (response.ok) {
                console.log('Emoji reaction removed');
                onEmojiSelect(-1, { id: -1, emoji: '', name: '', enabled: false });
                setIsOpen(false);
            } else {
                console.error('Failed to remove emoji reaction');
            }
        } catch (error) {
            console.error('Error removing emoji:', error);
        }
    };

    if (loading) {
        return <Loader2 className="h-4 w-4 animate-spin" />;
    }

    if (emojis.length === 0) {
        console.log('No emojis to display'); // Debug
        return null;
    }

    const userEmoji = userEmojiId ? emojis.find(e => e.id === userEmojiId) : null;
    const publicEmojis = emojis.filter(e => !e.admin_only);
    const adminEmojis = emojis.filter(e => e.admin_only);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    disabled={disabled}
                    className="text-xl hover:scale-125 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                    title={userEmoji ? `A te emojid: ${userEmoji.emoji}` : 'Emoji kiválasztása'}
                >
                    {userEmoji ? userEmoji.emoji : '😊'}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start">
                <div className="flex flex-col gap-3">
                    {userEmojiId && (
                        <div className="flex items-center justify-between p-2 bg-primary/10 rounded">
                            <span className="text-sm text-muted-foreground">A te emojid:</span>
                            <div className="flex items-center gap-2">
                                <span className="text-xl">{userEmoji?.emoji}</span>
                                <button
                                    onClick={handleRemoveEmoji}
                                    className="text-xs text-destructive hover:text-destructive/80"
                                    title="Emoji eltávolítása"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                    {publicEmojis.length > 0 && (
                        <div className="flex flex-nowrap gap-2 overflow-x-auto">
                            {publicEmojis.map((emoji) => (
                                <button
                                    key={emoji.id}
                                    onClick={() => handleEmojiClick(emoji)}
                                    className={`text-2xl hover:scale-150 transition-transform p-2 rounded hover:shadow-md flex-shrink-0 ${userEmojiId === emoji.id ? 'bg-primary/30 ring-2 ring-primary' : 'hover:bg-secondary'
                                        }`}
                                    title={emoji.name}
                                    type="button"
                                >
                                    {emoji.emoji}
                                </button>
                            ))}
                        </div>
                    )}
                    {adminEmojis.length > 0 && (
                        <div className="border-t pt-2">
                            <p className="text-xs text-muted-foreground mb-2 font-semibold">🔒 Admin Emojik</p>
                            <div className="flex flex-nowrap gap-2 overflow-x-auto">
                                {adminEmojis.map((emoji) => (
                                    <button
                                        key={emoji.id}
                                        onClick={() => handleEmojiClick(emoji)}
                                        className={`text-2xl hover:scale-150 transition-transform p-2 rounded hover:shadow-md flex-shrink-0 opacity-70 ${userEmojiId === emoji.id ? 'bg-primary/30 ring-2 ring-primary opacity-100' : 'hover:bg-secondary hover:opacity-100'
                                            }`}
                                        title={emoji.name}
                                        type="button"
                                    >
                                        {emoji.emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default EmojiSelector;
